import { useState } from 'react'

import {
  CompetenciesCompetenciesFinderSuggestForJobTreeNodeDocument,
  CompetenciesFinderFactorialCompetencyFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

import { sortFactorialCompetencies } from 'modules/competencies/lib/sortFactorialCompetencies'
import { getNodeId, getNodeType, isNodeType } from 'modules/job_catalog/lib/nodesUtils'

const MINIMUM_JOB_NODE_CHILDREN = 2
interface BaseCompetencySuggestionTarget {
  id: string | number
  title: string
  description: string
}

interface JobTreeNodeTarget extends BaseCompetencySuggestionTarget {
  nodeUuid: string
  treeNodeUuid: string
  childrenCount?: number
}

const isJobTreeNodeTarget = (target: CompetencySuggestionTarget): target is JobTreeNodeTarget => {
  return Boolean(
    target &&
      'nodeUuid' in target &&
      typeof target.nodeUuid === 'string' &&
      'treeNodeUuid' in target &&
      typeof target.treeNodeUuid === 'string' &&
      isNodeType(target.nodeUuid)
  )
}

export type CompetencySuggestionTarget = JobTreeNodeTarget

const canSuggestForJobTreeNodeTarget = (target: CompetencySuggestionTarget): boolean => {
  if (!isJobTreeNodeTarget(target)) return false

  const nodeType = getNodeType(target.nodeUuid)
  if (nodeType === 'jobcatalog_treerole') {
    return true
  }

  const childrenCount = target.childrenCount || 0

  if (nodeType === 'jobcatalog_treefunction' || nodeType === 'jobcatalog_treefamily') {
    return childrenCount >= MINIMUM_JOB_NODE_CHILDREN
  }

  return false
}

export const canSuggestForTarget = (target: CompetencySuggestionTarget) => {
  const hasName = target.title.trim().length > 0
  const hasDescription = target.description.trim().length > 0
  return hasName && hasDescription && canSuggestForJobTreeNodeTarget(target)
}

export const getTargetIdentifier = (target: CompetencySuggestionTarget): string => {
  return `target-${target.treeNodeUuid}-${target.nodeUuid}`
}

type UseSuggestionsParams = {
  target: CompetencySuggestionTarget
  mock?: boolean
  initialUuids: string[]
  limit?: number
}

type UseSuggestionsReturn = {
  suggestions: CompetenciesFinderFactorialCompetencyFragment[]
  loading: boolean
  error: boolean
}

export const useFactorialCompetenciesSuggestions = ({
  target,
  initialUuids,
}: UseSuggestionsParams): UseSuggestionsReturn => {
  const [initialUuidsSnapshot] = useState(() => (initialUuids || []).sort().join(','))

  const { company } = useCurrentSession()

  const { treeNodeUuid, nodeUuid } = target
  const isTargetValid = canSuggestForTarget(target)
  const treeId = getNodeId(treeNodeUuid)

  const { data, loading, error } = useQuery(
    CompetenciesCompetenciesFinderSuggestForJobTreeNodeDocument,
    {
      variables: {
        companyId: company.id,
        excludeUuids: initialUuidsSnapshot ? initialUuidsSnapshot.split(',') : [],
        treeId,
        treeNodeUuid: nodeUuid,
      },
      skip: !isTargetValid,
    }
  )

  const suggestions: CompetenciesFinderFactorialCompetencyFragment[] =
    data?.competencies?.jobCatalogNodeSuggestionsConnection?.edges?.map((edge) => ({
      importance: edge.node.importance,
      name: edge.node.name,
      uuid: edge.node.uuid,
      description: edge.node.description,
    })) || []

  const sortedSuggestions = sortFactorialCompetencies(suggestions)

  return {
    suggestions: sortedSuggestions,
    loading,
    error: !!error,
  }
}
